// src/ui/ChatTools.jsx — the burger-menu tools: Starred messages + smart Search,
// and the delete-confirmation sheet. One overlay panel, two tabs.

import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  X, Search as SearchIcon, Star, StarFill, Trash, Image as ImageIcon, Video, Mic,
  Doc, MapPin, Poll, Plane, Link as LinkIcon, Person, ChevronRight,
} from './icons';
import { timeLabel, dayLabel } from './format';

/* ══════════════════════════════════════════════════════════════════════════
   SMART SEARCH
   ══════════════════════════════════════════════════════════════════════════
   Two layers, like the app's SearchResultsScreen but friendlier about typing:

   1. CATEGORY WORDS. "photo", "video", "docs", "voice"… — the app matches these only
      when the whole query equals a keyword; here HALF A WORD is enough ("vid", "pic",
      "im"), which is what was asked for. A matched category returns that whole bucket.
   2. CONTENT. Case-insensitive substring across everything readable: text, captions,
      document names, poll questions, flight numbers and cities, link titles, contact
      names, and the sender's name.  */
const CATEGORIES = [
  { key: 'photo', label: 'Photos', words: ['photo', 'photos', 'image', 'images', 'picture', 'pictures', 'pic', 'pics'], Icon: ImageIcon,
    match: (m) => m.type === 'image' || m.type === 'images' },
  { key: 'video', label: 'Videos', words: ['video', 'videos', 'clip', 'clips'], Icon: Video,
    match: (m) => m.type === 'video' },
  { key: 'voice', label: 'Voice notes', words: ['audio', 'voice', 'recording', 'recordings', 'sound', 'memo'], Icon: Mic,
    match: (m) => m.type === 'audio' },
  { key: 'doc', label: 'Documents', words: ['document', 'documents', 'doc', 'docs', 'file', 'files', 'pdf'], Icon: Doc,
    match: (m) => m.type === 'document' },
  { key: 'link', label: 'Links', words: ['link', 'links', 'url', 'urls', 'website', 'websites'], Icon: LinkIcon,
    match: (m) => m.type === 'text' && (!!m.linkMetadata || /https?:\/\/\S+/i.test(m._plain || '')) },
  { key: 'location', label: 'Locations', words: ['location', 'locations', 'map', 'maps', 'place', 'places'], Icon: MapPin,
    match: (m) => m.type === 'location' },
  { key: 'poll', label: 'Polls', words: ['poll', 'polls', 'vote', 'votes', 'voting'], Icon: Poll,
    match: (m) => m.type === 'poll' },
  { key: 'flight', label: 'Flights', words: ['flight', 'flights', 'airline', 'plane'], Icon: Plane,
    match: (m) => m.type === 'flight' },
];

/** "vid" → the video category. Two letters minimum, so "i" doesn't light everything. */
function categoryFor(q) {
  if (!q || q.length < 2) return null;
  return CATEGORIES.find((c) => c.words.some((w) => w.startsWith(q))) || null;
}

function textOf(m, nameOf) {
  const bits = [
    m._plain, m._caption, m.documentName, m.contactName, m.question,
    m.flightData?.flightNumber, m.flightData?.origin?.city, m.flightData?.destination?.city,
    m.linkMetadata?.title, m.linkMetadata?.url,
    nameOf?.(m),
  ];
  return bits.filter((x) => typeof x === 'string' && x && !x.startsWith('🔒')).join(' ').toLowerCase();
}

const TYPE_ICON = {
  image: ImageIcon, images: ImageIcon, video: Video, audio: Mic, document: Doc,
  location: MapPin, poll: Poll, flight: Plane, contact: Person,
};

function rowSnippet(m) {
  if (m._plain && !m._plain.startsWith('🔒')) return m._plain;
  if (m._caption) return m._caption;
  switch (m.type) {
    case 'image': return 'Photo';
    case 'images': return 'Album';
    case 'video': return 'Video';
    case 'audio': return 'Voice message';
    case 'document': return m.documentName || 'Document';
    case 'location': return 'Location';
    case 'poll': return m.question || 'Poll';
    case 'flight': return m.flightData?.flightNumber ? `Flight ${m.flightData.flightNumber}` : 'Flight';
    case 'contact': return m.contactName || 'Contact';
    default: return m.text && !String(m.text).startsWith('🔒') ? m.text : 'Message';
  }
}

/** thumbnails ride on the doc itself (the same base64 previews the chat list uses) */
function thumbOf(m) {
  if (m.imageThumbnailBase64) return `data:image/jpeg;base64,${m.imageThumbnailBase64}`;
  if (m.videoThumbnailBase64) return `data:image/jpeg;base64,${m.videoThumbnailBase64}`;
  if (Array.isArray(m.batchThumbnails) && m.batchThumbnails[0]) return `data:image/jpeg;base64,${m.batchThumbnails[0]}`;
  if (m._media && (m.type === 'image')) return m._media;
  return null;
}

function ResultRow({ m, nameOf, myUid, onJump, trailing }) {
  const Icon = TYPE_ICON[m.type];
  const thumb = thumbOf(m);
  return (
    <button className="ct-row" onClick={() => onJump?.(m.id)}>
      {thumb
        ? <img className="ct-thumb" src={thumb} alt="" />
        : <span className="ct-ico">{Icon ? <Icon width={16} height={16} /> : <SearchIcon width={16} height={16} />}</span>}
      <span className="ct-body">
        <span className="ct-top">
          <b>{m.senderId === myUid ? 'You' : (nameOf?.(m) || 'Allo user')}</b>
          <i>{timeLabel(m.timestamp)}</i>
        </span>
        <span className="ct-snippet">{rowSnippet(m)}</span>
      </span>
      {trailing}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */

export default function ChatTools({ mode, onModeChange, onClose, msgs, starred, myUid, nameOf, onJump, onUnstar }) {
  const [q, setQ] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (mode === 'search') inputRef.current?.focus(); }, [mode]);

  const query = q.trim().toLowerCase();
  const activeCat = useMemo(() => categoryFor(query), [query]);

  // everything searchable, newest first (msgs arrive oldest-first)
  const pool = useMemo(
    () => msgs.filter((m) => !m.isDeleted && !m.deleted && m.type !== 'system' && !m._sep).slice().reverse(),
    [msgs],
  );

  const counts = useMemo(() => {
    const out = {};
    for (const c of CATEGORIES) out[c.key] = pool.filter(c.match).length;
    return out;
  }, [pool]);

  const results = useMemo(() => {
    if (mode !== 'search') return [];
    if (activeCat) return pool.filter(activeCat.match);
    if (!query) return [];
    return pool.filter((m) => textOf(m, nameOf).includes(query));
  }, [mode, pool, query, activeCat, nameOf]);

  const starredMsgs = useMemo(
    () => pool.filter((m) => starred?.has(m.id)),
    [pool, starred],
  );

  /* day-group a flat list: [{day, items}] */
  const grouped = (list) => {
    const out = [];
    for (const m of list) {
      const day = dayLabel(m.timestamp);
      if (!out.length || out[out.length - 1].day !== day) out.push({ day, items: [] });
      out[out.length - 1].items.push(m);
    }
    return out;
  };

  const body = mode === 'starred' ? (
    starredMsgs.length ? grouped(starredMsgs).map((g) => (
      <div key={g.day}>
        <div className="ct-day">{g.day}</div>
        {g.items.map((m) => (
          <ResultRow
            key={m.id} m={m} nameOf={nameOf} myUid={myUid} onJump={onJump}
            trailing={(
              <span
                className="ct-unstar" title="Unstar" role="button" tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onUnstar?.(m.id); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onUnstar?.(m.id); } }}
              >
                <StarFill width={15} height={15} />
              </span>
            )}
          />
        ))}
      </div>
    )) : (
      <div className="ct-empty">
        <Star width={26} height={26} />
        <b>No starred messages</b>
        <span>Hover a message and tap the star — it appears here and on your phone.</span>
      </div>
    )
  ) : (
    <>
      {/* category chips: a browsable media index when idle, a hint of what matched when typing */}
      <div className="ct-chips">
        {CATEGORIES.filter((c) => counts[c.key] > 0).map((c) => (
          <button
            key={c.key}
            className={`ct-chip ${activeCat?.key === c.key ? 'on' : ''}`}
            onClick={() => setQ(activeCat?.key === c.key ? '' : c.words[0])}
          >
            <c.Icon width={14} height={14} />
            {c.label}
            <i>{counts[c.key]}</i>
          </button>
        ))}
      </div>
      {results.length ? grouped(results).map((g) => (
        <div key={g.day}>
          <div className="ct-day">{g.day}</div>
          {g.items.map((m) => (
            <ResultRow key={m.id} m={m} nameOf={nameOf} myUid={myUid} onJump={onJump} />
          ))}
        </div>
      )) : query ? (
        <div className="ct-empty">
          <SearchIcon width={26} height={26} />
          <b>Nothing matches “{q.trim()}”</b>
          <span>Try part of a word — “vid”, “pdf”, “pic” — or a person’s name.</span>
        </div>
      ) : (
        <div className="ct-empty subtle">
          <span>Search this conversation — messages, names, files… or just “photos”.</span>
        </div>
      )}
    </>
  );

  return (
    <div className="chat-tools">
      <div className="ct-head">
        <div className="ct-tabs">
          <button className={mode === 'starred' ? 'on' : ''} onClick={() => onModeChange('starred')}>
            <StarFill width={14} height={14} /> Starred
          </button>
          <button className={mode === 'search' ? 'on' : ''} onClick={() => onModeChange('search')}>
            <SearchIcon width={14} height={14} /> Search
          </button>
        </div>
        <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
      </div>

      {mode === 'search' && (
        <div className="ct-search">
          <SearchIcon width={16} height={16} />
          <input
            ref={inputRef}
            placeholder="Search — try “photos”, a name, a word…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && <button className="ct-clear" onClick={() => setQ('')} aria-label="Clear"><X width={14} height={14} /></button>}
        </div>
      )}

      <div className="ct-list">{body}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   DELETE CONFIRMATION
   ══════════════════════════════════════════════════════════════════════════
   The same two-or-one choice the app offers: "for everyone" appears only while the
   recipient hasn't seen the message (and never on someone else's message or a call). */
export function DeleteSheet({ canEveryone, eraseMode, onForMe, onForEveryone, onCancel }) {
  return (
    <div className="del-veil" onClick={onCancel}>
      <div className="del-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="del-ico"><Trash width={20} height={20} /></div>
        <b>Delete this message?</b>
        <span className="del-sub">
          {eraseMode
            ? 'It will be removed from this chat on all your devices.'
            : canEveryone
              ? 'They haven’t seen it yet — you can still take it back for both of you.'
              : 'It will disappear for you; the other side keeps their copy.'}
        </span>
        {canEveryone && (
          <button className="del-btn danger" onClick={onForEveryone}>
            Delete for everyone <ChevronRight width={15} height={15} />
          </button>
        )}
        <button className="del-btn" onClick={onForMe}>
          {eraseMode ? 'Delete' : 'Delete for me'}
        </button>
        <button className="del-btn ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
